import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store,
  ArrowLeft,
  Plus,
  RefreshCw,
  AlertCircle,
  User,
  Clock,
  Shield,
  Star,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/utils';
import {
  useP2POffers,
  useMyP2POffers,
  useAcceptP2POffer,
  useCancelP2POffer,
  usePrimaryVirtualAccount,
  P2POffer,
} from '../../hooks/useCrypto';

const CRYPTO_OPTIONS = [
  { value: '', label: 'Todas as moedas' },
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'ETH', label: 'Ethereum (ETH)' },
  { value: 'USDT', label: 'Tether (USDT)' },
  { value: 'USDC', label: 'USD Coin (USDC)' },
  { value: 'SOL', label: 'Solana (SOL)' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Comprar e Vender' },
  { value: 'BUY', label: 'Comprar' },
  { value: 'SELL', label: 'Vender' },
];

export function BankingCryptoMarketplace() {
  const navigate = useNavigate();
  const { hasCategory } = useAuthStore();
  
  // Get primary virtual account ID from banking API
  const { data: primaryAccount, isLoading: accountLoading } = usePrimaryVirtualAccount();
  const virtualAccountId = primaryAccount?.id || '';

  const [activeTab, setActiveTab] = useState<'market' | 'my-offers'>('market');
  const [filters, setFilters] = useState<{ crypto?: string; type?: string }>({});
  const [selectedOffer, setSelectedOffer] = useState<P2POffer | null>(null);
  const [acceptAmount, setAcceptAmount] = useState('');

  const { data: offersData, isLoading: offersLoading } = useP2POffers(filters);
  const { data: myOffersData, isLoading: isLoadingMyOffers } = useMyP2POffers(virtualAccountId);
  const acceptMutation = useAcceptP2POffer(virtualAccountId);
  const cancelMutation = useCancelP2POffer(virtualAccountId);
  
  // Ensure offers are always arrays
  const offers = Array.isArray(offersData) ? offersData : [];
  const myOffers = Array.isArray(myOffersData) ? myOffersData : [];
  
  const isLoading = accountLoading || offersLoading;

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Crypto não habilitado
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
          </p>
        </Card>
      </div>
    );
  }
  
  // Check if user has a virtual account
  if (!accountLoading && !virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa ter uma conta virtual para acessar o marketplace P2P.
          </p>
        </Card>
      </div>
    );
  }

  const handleAcceptOffer = async () => {
    if (!selectedOffer || !acceptAmount) return;
    
    try {
      await acceptMutation.mutateAsync({
        offerId: selectedOffer.id,
        amount: parseFloat(acceptAmount),
      });
      setSelectedOffer(null);
      setAcceptAmount('');
    } catch (error) {
      console.error('Error accepting offer:', error);
    }
  };

  const handleCancelOffer = async (offerId: string) => {
    try {
      await cancelMutation.mutateAsync(offerId);
    } catch (error) {
      console.error('Error canceling offer:', error);
    }
  };

  const loading = activeTab === 'market' ? isLoading : isLoadingMyOffers;
  const displayOffers = activeTab === 'market' ? offers : myOffers;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/banking/crypto')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
            <Store className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Marketplace P2P</h1>
            <p className="text-surface-400">Compre e venda crypto diretamente</p>
          </div>
        </div>
        <Button onClick={() => navigate('/banking/crypto/sell')}>
          <Plus className="w-4 h-4 mr-2" />
          Criar Oferta
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'market' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('market')}
        >
          Marketplace
        </Button>
        <Button
          variant={activeTab === 'my-offers' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('my-offers')}
        >
          Minhas Ofertas ({myOffers.length})
        </Button>
      </div>

      {/* Filters (only for market tab) */}
      {activeTab === 'market' && (
        <div className="flex flex-wrap gap-4 items-end">
          <Select
            options={CRYPTO_OPTIONS}
            value={filters.crypto || ''}
            onChange={(e) => setFilters(f => ({ ...f, crypto: e.target.value || undefined }))}
          />
          <Select
            options={TYPE_OPTIONS}
            value={filters.type || ''}
            onChange={(e) => setFilters(f => ({ ...f, type: e.target.value || undefined }))}
          />
        </div>
      )}

      {/* Offers List */}
      {displayOffers.length === 0 ? (
        <Card className="text-center py-12">
          <Store className="w-12 h-12 mx-auto mb-4 text-surface-500" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">
            {activeTab === 'market' ? 'Nenhuma oferta disponível' : 'Você não tem ofertas'}
          </h3>
          <p className="text-surface-400 mb-4">
            {activeTab === 'market' 
              ? 'Novas ofertas aparecerão aqui' 
              : 'Crie uma oferta para começar a vender'}
          </p>
          {activeTab === 'my-offers' && (
            <Button onClick={() => navigate('/banking/crypto/sell')}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Oferta
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {displayOffers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              isOwner={activeTab === 'my-offers'}
              onAccept={() => setSelectedOffer(offer)}
              onCancel={() => handleCancelOffer(offer.id)}
              isCanceling={cancelMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Accept Offer Modal */}
      <Modal
        isOpen={!!selectedOffer}
        onClose={() => {
          setSelectedOffer(null);
          setAcceptAmount('');
        }}
        title={`${selectedOffer?.type === 'SELL' ? 'Comprar' : 'Vender'} ${selectedOffer?.crypto}`}
      >
        {selectedOffer && (
          <div className="space-y-6">
            {/* Offer Info */}
            <Card className="bg-surface-800/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary-400" />
                </div>
                <div>
                  <p className="font-medium text-surface-100">{selectedOffer.sellerName}</p>
                  <div className="flex items-center gap-2 text-sm text-surface-400">
                    <Star className="w-3 h-3 text-yellow-400" />
                    <span>{selectedOffer.sellerRating?.toFixed(1) || 'N/A'}</span>
                    <span>•</span>
                    <span>{selectedOffer.sellerTrades || 0} trades</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-surface-400 text-sm">Preço</p>
                  <p className="text-xl font-bold text-surface-100">
                    {formatCurrency(selectedOffer.pricePerUnit / 100)}
                  </p>
                  <p className="text-surface-500 text-xs">por {selectedOffer.crypto}</p>
                </div>
                <div>
                  <p className="text-surface-400 text-sm">Disponível</p>
                  <p className="text-xl font-bold text-surface-100">
                    {selectedOffer.availableAmount.toFixed(6)}
                  </p>
                  <p className="text-surface-500 text-xs">{selectedOffer.crypto}</p>
                </div>
              </div>
            </Card>

            {/* Amount Input */}
            <Input
              label={`Quantidade de ${selectedOffer.crypto}`}
              type="number"
              placeholder="0.000000"
              value={acceptAmount}
              onChange={(e) => setAcceptAmount(e.target.value)}
              max={selectedOffer.availableAmount}
              step="0.000001"
            />

            {/* Total */}
            {acceptAmount && parseFloat(acceptAmount) > 0 && (
              <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/30">
                <p className="text-surface-400 text-sm">Total a {selectedOffer.type === 'SELL' ? 'pagar' : 'receber'}</p>
                <p className="text-2xl font-bold text-primary-400">
                  {formatCurrency((parseFloat(acceptAmount) * selectedOffer.pricePerUnit) / 100)}
                </p>
              </div>
            )}

            {/* Security Notice */}
            <div className="p-4 rounded-lg bg-surface-800/50 flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-surface-100 font-medium">Transação Segura</p>
                <p className="text-surface-400 text-sm">
                  Os fundos ficam em custódia até a confirmação de ambas as partes.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setSelectedOffer(null)}>
                Cancelar
              </Button>
              <Button
                onClick={handleAcceptOffer}
                disabled={!acceptAmount || parseFloat(acceptAmount) <= 0 || acceptMutation.isPending}
              >
                {acceptMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  `Confirmar ${selectedOffer.type === 'SELL' ? 'Compra' : 'Venda'}`
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// OfferCard Component
interface OfferCardProps {
  offer: P2POffer;
  isOwner: boolean;
  onAccept: () => void;
  onCancel: () => void;
  isCanceling: boolean;
}

function OfferCard({ offer, isOwner, onAccept, onCancel, isCanceling }: OfferCardProps) {
  const getTypeBadge = (type: string) => {
    if (type === 'SELL') {
      return <Badge className="bg-green-500/20 text-green-400">Venda</Badge>;
    }
    return <Badge className="bg-blue-500/20 text-blue-400">Compra</Badge>;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500/20 text-green-400">Ativa</Badge>;
      case 'COMPLETED':
        return <Badge className="bg-surface-500/20 text-surface-400">Concluída</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-red-500/20 text-red-400">Cancelada</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="hover:border-primary-500/50 transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Seller Info */}
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
            <User className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-surface-100">{offer.sellerName}</span>
              {getTypeBadge(offer.type)}
              {isOwner && getStatusBadge(offer.status)}
            </div>
            <div className="flex items-center gap-2 text-sm text-surface-400">
              <Star className="w-3 h-3 text-yellow-400" />
              <span>{offer.sellerRating?.toFixed(1) || 'N/A'}</span>
              <span>•</span>
              <span>{offer.sellerTrades || 0} trades</span>
            </div>
          </div>
        </div>

        {/* Crypto Info */}
        <div className="text-center">
          <p className="text-surface-500 text-xs">Moeda</p>
          <p className="text-lg font-bold text-surface-100">{offer.crypto}</p>
        </div>

        {/* Price */}
        <div className="text-center">
          <p className="text-surface-500 text-xs">Preço</p>
          <p className="text-lg font-bold text-primary-400">
            {formatCurrency(offer.pricePerUnit / 100)}
          </p>
        </div>

        {/* Available */}
        <div className="text-center">
          <p className="text-surface-500 text-xs">Disponível</p>
          <p className="text-lg font-bold text-surface-100">
            {offer.availableAmount.toFixed(4)}
          </p>
        </div>

        {/* Limits */}
        <div className="text-center">
          <p className="text-surface-500 text-xs">Limites</p>
          <p className="text-sm text-surface-300">
            {formatCurrency((offer.minAmount || 0) / 100)} - {formatCurrency((offer.maxAmount || offer.availableAmount * offer.pricePerUnit) / 100)}
          </p>
        </div>

        {/* Actions */}
        <div>
          {isOwner ? (
            offer.status === 'ACTIVE' && (
              <Button
                variant="danger"
                onClick={onCancel}
                disabled={isCanceling}
              >
                {isCanceling ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Cancelar'}
              </Button>
            )
          ) : (
            <Button onClick={onAccept}>
              {offer.type === 'SELL' ? 'Comprar' : 'Vender'}
            </Button>
          )}
        </div>
      </div>

      {/* Time */}
      <div className="mt-4 pt-4 border-t border-surface-800 flex items-center gap-2 text-surface-500 text-sm">
        <Clock className="w-4 h-4" />
        <span>Criada em {new Date(offer.createdAt).toLocaleString('pt-BR')}</span>
      </div>
    </Card>
  );
}
